import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "首页" },
  { to: "/category/movies", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center gap-4 md:gap-8">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-lg md:text-xl">
              <i className="ri-heart-3-fill"></i>
            </span>
            <span
              className={`font-heading text-xl md:text-2xl font-bold tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              爱情岛
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `px-3 lg:px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-primary-600"
                      : solid
                        ? "text-foreground-700 hover:text-primary-600"
                        : "text-background-100 hover:text-background-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={handleSearch}
            className="hidden lg:flex flex-1 max-w-xs items-center ml-auto"
          >
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索爱情电影、电视剧"
                className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </form>

          <div className="flex items-center gap-2 ml-auto lg:ml-0">
            <Link
              to="/search"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-lg cursor-pointer ${
                solid ? "text-foreground-700 hover:bg-background-100" : "text-background-50"
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-xl cursor-pointer ${
                solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50"
              }`}
              aria-label="菜单"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden pb-4">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative w-full">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索爱情电影、电视剧"
                  className="w-full h-11 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
            </form>
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm font-medium rounded-md cursor-pointer ${
                      isActive
                        ? "bg-primary-50 text-primary-600"
                        : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}